import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, DatePicker, Form, Input, InputNumber, message, Modal, Select, Space, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { doc, getDoc } from "firebase/firestore";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import QuantityControl from "components/shared-components/QuantityControl";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { updateCountedStock } from "../../../../../functions/api/stock";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { FIRESTORE_PRODUCTS_STOCK_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import { createIncomingGoodsEvent, logCrateEvent, sanitizeCrateDocId } from "../../../../../functions/crateStock";
import { MobileModalFooter, MobileModalTitle, MobileSectionLabel } from "./mobileModalStyles";

const STATUS_IN_USE = "in_use";
const STATUS_AVAILABLE = "available";
const { Text } = Typography;

const CRATE_BARCODE_LENGTH = 99;
const MIN_PRODUCT_BARCODE_LENGTH = 8;
const MAX_PRODUCT_BARCODE_LENGTH = 13;

const getProductCompany = (product) => product?.company || product?.productCompany;

const AddCrateStockModal = ({
  open,
  onCancel,
  crateStocks,
  upsertCrateStock,
  updateCrateStock,
  addCrateEvent,
  mode = "desktop",
  initialCrateId = null,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { products, getProductByBarcode } = useProducts();
  const { uid, email, profile } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [validatedCrateId, setValidatedCrateId] = useState(null);
  const [mobileStep, setMobileStep] = useState(1);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [crateDropdownValue, setCrateDropdownValue] = useState(null);
  // Standaard vandaag; aanpasbaar als het product eerder verpakt is (bepaalt de THT, o.a. in de DESADV).
  const [packageDay, setPackageDay] = useState(() => dayjs());
  const [editingPackageDay, setEditingPackageDay] = useState(false);
  const productScanBufferRef = useRef("");
  const productScanTimerRef = useRef(null);

  const productSelectRef = useRef(null);
  const crateBarcodeRef = useRef(null);
  const crateSelectRef = useRef(null);
  const qtyRef = useRef(null);
  const productBarcodeRef = useRef(null);

  const isMobileMode = mode === "mobile";
  const isAutoStarted = Boolean(initialCrateId);
  const isProductStep = !isMobileMode || mobileStep === 2;
  const isCrateLocked = isMobileMode && (isAutoStarted || mobileStep === 2);

  const isCrateSelectStep = isMobileMode && !isAutoStarted && mobileStep === 1;

  const watchedCrateId = Form.useWatch("crateId", form);
  const selectedProductId = Form.useWatch("productId", form);
  const watchedQuantity = Form.useWatch("quantity", form);

  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null;
    return products.find((p) => p.id === selectedProductId) || null;
  }, [products, selectedProductId]);

  const derivedCompany = useMemo(() => {
    return getProductCompany(selectedProduct);
  }, [selectedProduct]);

  const selectedCrate = useMemo(() => {
    if (!watchedCrateId) return null;
    const crateId = String(watchedCrateId).trim();
    if (!crateId) return null;

    const crateDocId = sanitizeCrateDocId(crateId);
    return (crateStocks || []).find((c) => String(c.crateId).trim() === crateId || c.id === crateDocId) || null;
  }, [crateStocks, watchedCrateId]);

  const detectedCompany = selectedCrate?.company || derivedCompany;

  const isCrateValidated = useMemo(() => {
    if (!validatedCrateId) return false;
    if (!watchedCrateId) return false;
    return String(validatedCrateId).trim() === String(watchedCrateId).trim();
  }, [validatedCrateId, watchedCrateId]);

  const isProductLocked = Boolean(selectedProductId);

  const availableCrates = useMemo(() => {
    return (crateStocks || [])
      .filter((c) => {
        const itemsCount = typeof c.itemsCount === "number" ? c.itemsCount : 0;
        const isAvailable = c.status === STATUS_AVAILABLE && itemsCount === 0;
        const isLost = c.status === "lost";
        const companyMatches = !c.company || !derivedCompany || c.company === derivedCompany;

        return isAvailable && !isLost && companyMatches;
      })
      .sort((a, b) => String(a.crateId || "").localeCompare(String(b.crateId || "")));
  }, [crateStocks, derivedCompany]);

  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    return products;
  }, [products]);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        quantity: 1,
      });
      setValidatedCrateId(null);
      setBarcodeInput("");
      setCrateDropdownValue(null);
      setPackageDay(dayjs());
      setEditingPackageDay(false);
      setMobileStep(isMobileMode && !initialCrateId ? 1 : 2);
      setTimeout(() => {
        if (isMobileMode) {
          // In mobile mode we likely already have a crate. If not, focus crate.
          if (initialCrateId) {
            productBarcodeRef.current?.focus?.();
          } else {
            crateBarcodeRef.current?.focus?.();
          }
        } else {
          crateBarcodeRef.current?.focus?.();
        }
      }, 0);
    } else {
      form.resetFields();
      setValidatedCrateId(null);
      setBarcodeInput("");
      setCrateDropdownValue(null);
      setMobileStep(1);
    }
  }, [open, form, initialCrateId, isMobileMode]);

  useEffect(() => {
    if (!open) return;
    if (!initialCrateId) return;

    // If opened from a scan (desktop or mobile), prefill & validate.
    form.setFieldsValue({ crateId: initialCrateId });
    setBarcodeInput(initialCrateId || "");
    validateCrateAndMaybeSetValidated(initialCrateId, { showMessage: true, focusNext: true });
  }, [form, initialCrateId, open]);

  useEffect(() => {
    return () => {
      if (productScanTimerRef.current) {
        clearTimeout(productScanTimerRef.current);
        productScanTimerRef.current = null;
      }
    };
  }, []);

  const confirmCancel = () => {
    Modal.confirm({
      title: t("crate_stock.cancel_confirm_title"),
      content: t("crate_stock.cancel_confirm_content"),
      okText: t("crate_stock.yes"),
      cancelText: t("crate_stock.no"),
      onOk: () => {
        onCancel?.();
      },
    });
  };

  useEffect(() => {
    // Any change to crateId invalidates previously validated crate
    if (!watchedCrateId) {
      setValidatedCrateId(null);
      return;
    }
    if (validatedCrateId && String(validatedCrateId).trim() !== String(watchedCrateId).trim()) {
      setValidatedCrateId(null);
      form.setFieldsValue({ productId: undefined });
    }
  }, [watchedCrateId, validatedCrateId, form]);

  const validateCrateAndMaybeSetValidated = (rawCrateId, { showMessage = true, focusNext = false } = {}) => {
    const crateId = String(rawCrateId || "").trim();
    if (!crateId) return false;

    if (crateId.length > CRATE_BARCODE_LENGTH) {
      setValidatedCrateId(null);
      return false;
    }

    if (!/^[0-9]+$/.test(crateId)) {
      if (showMessage) message.error(t("crate_stock.crate_barcode_must_be_numeric"));
      form.setFieldsValue({ crateId: undefined });
      setBarcodeInput("");
      setValidatedCrateId(null);
      return false;
    }

    const crateDocId = sanitizeCrateDocId(crateId);
    const existingCrate = (crateStocks || []).find((c) => String(c.crateId).trim() === crateId || c.id === crateDocId);

    if (existingCrate?.status === "lost") {
      if (showMessage) message.error(t("crate_stock.crate_marked_lost"));
      form.setFieldsValue({ crateId: undefined });
      setBarcodeInput("");
      return false;
    }

    if (existingCrate) {
      const existingItemsCount = typeof existingCrate.itemsCount === "number" ? existingCrate.itemsCount : 0;
      const isEmpty = existingCrate.status === STATUS_AVAILABLE && existingItemsCount === 0;

      if (!isEmpty) {
        if (showMessage) message.error(t("crate_stock.crate_not_empty"));
        form.setFieldsValue({ crateId: undefined });
        setBarcodeInput("");
        return false;
      }
    }

    // Existing empty crate OR unknown crate (new): allow user to continue
    setValidatedCrateId(crateId);

    if (focusNext) {
      setTimeout(() => productSelectRef.current?.focus?.(), 0);
    }

    return true;
  };

  useEffect(() => {
    if (!open) return;
    if (!isMobileMode) return;
    if (!initialCrateId) return;

    form.setFieldsValue({ crateId: initialCrateId });
    setBarcodeInput(initialCrateId || "");
    // It should already be valid because we check in Crates tab, but keep it safe.
    validateCrateAndMaybeSetValidated(initialCrateId, { showMessage: true });
  }, [form, initialCrateId, isMobileMode, open]);

  const handleProductBarcodeScanned = useCallback(
    (barcode) => {
      const trimmed = String(barcode || "").trim();
      if (!trimmed) return;
      if (trimmed.length < MIN_PRODUCT_BARCODE_LENGTH || trimmed.length > MAX_PRODUCT_BARCODE_LENGTH) {
        message.error(t("crate_stock.invalid_product_barcode"));
        return;
      }

      const product = getProductByBarcode?.(trimmed);
      if (!product) {
        message.error(t("crate_stock.unknown_product_barcode"));
        return;
      }

      if (!selectedProductId) {
        form.setFieldsValue({ productId: product.id, quantity: 1 });
        setTimeout(() => {
          qtyRef.current?.focus?.();
        }, 0);
        return;
      }

      if (selectedProductId === product.id) {
        const nextQty = Number(watchedQuantity || 0) + 1;
        form.setFieldsValue({ quantity: nextQty });
        return;
      }

      message.error(t("crate_stock.only_one_product_type_per_crate"));
    },
    [form, getProductByBarcode, selectedProductId, t, watchedQuantity],
  );

  useEffect(() => {
    if (!open) return;
    if (!isProductStep) return;

    const clearScanBuffer = () => {
      productScanBufferRef.current = "";
      if (productScanTimerRef.current) {
        clearTimeout(productScanTimerRef.current);
        productScanTimerRef.current = null;
      }
    };

    const handleKeyDown = (e) => {
      if (!open) return;

      // Only handle product scanning after crate is validated.
      if (!isCrateValidated) return;

      if (/^[0-9]$/.test(e.key)) {
        // Barcode scanners typically send keys very fast. Keep a short timeout; if user types slowly,
        // the buffer clears and we won't treat it as a scan.
        productScanBufferRef.current = `${productScanBufferRef.current}${e.key}`.slice(-MAX_PRODUCT_BARCODE_LENGTH);

        if (productScanTimerRef.current) clearTimeout(productScanTimerRef.current);
        productScanTimerRef.current = setTimeout(() => {
          clearScanBuffer();
        }, 120);
        return;
      }

      if (e.key === "Enter") {
        const code = productScanBufferRef.current;
        clearScanBuffer();
        if (code) handleProductBarcodeScanned(code);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearScanBuffer();
    };
  }, [handleProductBarcodeScanned, isCrateValidated, isProductStep, open]);

  const goToProductStep = () => {
    const crateId = form.getFieldValue("crateId");
    const ok = validateCrateAndMaybeSetValidated(crateId, { showMessage: true });
    if (!ok) return;

    setMobileStep(2);
    setTimeout(() => {
      productBarcodeRef.current?.focus?.();
    }, 0);
  };

  const goBackToCrateStep = () => {
    setMobileStep(1);
    setValidatedCrateId(null);
    setBarcodeInput("");
    setCrateDropdownValue(null);
    form.setFieldsValue({ productId: undefined, quantity: 1 });
    setTimeout(() => {
      crateBarcodeRef.current?.focus?.();
    }, 0);
  };

  const handleCrateCheckClick = () => {
    const crateId = form.getFieldValue("crateId");
    if (!crateId) return;
    setCrateDropdownValue(null);
    validateCrateAndMaybeSetValidated(crateId, { showMessage: true, focusNext: true });
  };

  const computeItemsCount = (items) => {
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const packageDate = packageDay.format("YYYY-MM-DD");
      const packageDateUnix = packageDay.startOf("day").unix();
      const dateId = dayjs().format("DDMMYYYY");

      const product = products.find((p) => p.id === values.productId);
      if (!product) {
        message.error(t("crate_stock.product_not_found"));
        return;
      }

      const productCompany = getProductCompany(product);
      if (!productCompany) {
        message.error(t("crate_stock.product_has_no_company"));
        return;
      }

      const crateId = String(values.crateId || "").trim();
      if (!crateId) {
        message.error(t("crate_stock.crate_id_required"));
        return;
      }

      const crateDocId = sanitizeCrateDocId(crateId);

      const existingCrate = (crateStocks || []).find(
        (c) => String(c.crateId).trim() === crateId || c.id === crateDocId,
      );
      if (existingCrate?.status === "lost") {
        message.error(t("crate_stock.crate_marked_lost"));
        return;
      }

      if (existingCrate) {
        const existingItemsCount = typeof existingCrate.itemsCount === "number" ? existingCrate.itemsCount : 0;
        const isEmpty = existingCrate.status === STATUS_AVAILABLE && existingItemsCount === 0;

        if (!isEmpty) {
          message.error(t("crate_stock.crate_not_empty"));
          return;
        }
      }

      // Enforce: crate may contain only 1 product type (and we only allow empty crates)
      const existingItems = Array.isArray(existingCrate?.items) ? existingCrate.items : [];
      if (existingItems.length > 0 && existingItems.some((i) => i?.productId && i.productId !== product.id)) {
        message.error(t("crate_stock.crate_contains_other_product_type"));
        return;
      }

      if (existingCrate?.company && existingCrate.company !== productCompany) {
        message.error(t("crate_stock.crate_assigned_other_company"));
        return;
      }

      const newItem = {
        productId: product.id,
        productName: product.productName || product.name?.en || product.name?.nl || null,
        barcode: product.barcode || null,
        quantity: Number(values.quantity),
        packageDate,
        packageDateUnix,
        createdByUid: uid || null,
        createdBy: email || null,
        createdByName:
          profile?.displayName || profile?.name || profile?.fullName || profile?.email || email || uid || null,
      };

      // Because we only allow new/empty crates and 1 product type per crate, snapshot stays single-item
      const nextItems = [newItem];
      const nextItemsCount = computeItemsCount(nextItems);

      const token = await getFirebaseIDToken();
      const config = { headers: { Authorization: token } };

      const stockDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_PRODUCTS_STOCK_TABLE,
        dateId,
      );

      const stockDoc = await getDoc(stockDocRef);
      const currentCountedStock = Number(stockDoc.data()?.products?.[product.id]?.countedStock || 0);
      const nextCountedStock = currentCountedStock + Number(values.quantity || 0);

      // trackAddedRemoved = true because this is incoming goods being registered in warehouse
      await updateCountedStock(product.id, dateId, nextCountedStock, COMPANY_COUNTRY_CODE, token, true);

      if (!existingCrate) {
        await upsertCrateStock(
          crateDocId,
          {
            crateId,
            company: productCompany,
            status: STATUS_IN_USE,
            items: nextItems,
            itemsCount: nextItemsCount,
          },
          uid || email || null,
        );
      } else {
        await updateCrateStock(
          existingCrate.id,
          {
            crateId,
            company: existingCrate.company || productCompany,
            status: nextItemsCount > 0 ? STATUS_IN_USE : "available",
            items: nextItems,
            itemsCount: nextItemsCount,
          },
          uid || email || null,
        );
      }

      const previousQuantity = 0;
      const newQuantity = Number(values.quantity);
      const delta = newQuantity - previousQuantity;

      // Use utility to create and log standardized incoming goods event
      const incomingGoodsEvent = createIncomingGoodsEvent({
        crateId,
        crateDocId,
        productId: product.id,
        productName: product.productName || product.name?.en || null,
        productBarcode: product.barcode || null,
        quantityBefore: previousQuantity,
        quantity: newQuantity,
        delta,
        packageDate,
        packageDateUnix,
        company: productCompany,
        createdByUid: uid || null,
        createdBy: email || null,
      });

      await logCrateEvent({ crateDocId: existingCrate?.id || crateDocId, eventData: incomingGoodsEvent });

      message.success(t("crate_stock.incoming_goods_registered"));
      form.resetFields(["productId", "quantity", "crateId"]);
      form.setFieldsValue({ quantity: 1 });
      setBarcodeInput("");
      setCrateDropdownValue(null);
      onCancel?.();
    } catch (e) {
      // Form validation errors already shown
      if (e?.errorFields) return;

      const axiosCode = e?.code;
      const axiosStatus = e?.response?.status;
      const isServerUnreachable =
        axiosCode === "ERR_NETWORK" || axiosCode === "ECONNABORTED" || axiosCode === "ERR_BAD_RESPONSE";
      const isConnectionRefused = typeof e?.message === "string" && e.message.includes("ERR_CONNECTION_REFUSED");

      if (isServerUnreachable || isConnectionRefused || axiosStatus) {
        message.error(t("crate_stock.stock_update_failed_server"));
      } else {
        message.error(t("crate_stock.stock_update_failed_server"));
      }
      console.error("Failed to register incoming goods / update stock:", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        isMobileMode ? (
          <MobileModalTitle>{t("crate_stock.register_incoming_goods_title")}</MobileModalTitle>
        ) : (
          t("crate_stock.register_incoming_goods_title")
        )
      }
      open={open}
      onCancel={confirmCancel}
      closable
      maskClosable={false}
      footer={
        isMobileMode ? (
          // Mobiel: twee grote knoppen, zoals bij het orderpicken. Annuleren kan altijd via het kruisje.
          <MobileModalFooter
            secondary={
              mobileStep === 2 && !isAutoStarted
                ? { label: t("crate_stock.back"), props: { onClick: goBackToCrateStep, disabled: submitting } }
                : { label: t("cancel"), props: { onClick: confirmCancel, disabled: submitting } }
            }
            primary={
              mobileStep === 1
                ? { label: t("crate_stock.next"), props: { onClick: goToProductStep, disabled: submitting } }
                : { label: t("crate_stock.submit"), props: { onClick: onSubmit, loading: submitting } }
            }
          />
        ) : (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <div>
              {isMobileMode && !isAutoStarted && (
                <Button size="large" onClick={goBackToCrateStep} disabled={submitting}>
                  {t("crate_stock.back")}
                </Button>
              )}
            </div>
            <Space>
              <Button size="large" onClick={confirmCancel} disabled={submitting}>
                {t("cancel")}
              </Button>
              <Button size="large" type="primary" onClick={onSubmit} loading={submitting}>
                {t("crate_stock.submit")}
              </Button>
            </Space>
          </Space>
        )
      }
    >
      <Form layout="vertical" form={form}>
        {!isMobileMode && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">{t("crate_stock.company_auto_set_hint")}</Text>
            <div>
              <Text strong>{t("crate_stock.company_label")}</Text> <Text>{detectedCompany || "-"}</Text>
            </div>
          </div>
        )}

        {isMobileMode ? (
          mobileStep === 1 && !isAutoStarted ? (
            <>
              <Form.Item
                name="crateId"
                hidden
                rules={[{ required: true, message: t("crate_stock.scan_or_select_crate") }]}
              >
                <Input />
              </Form.Item>

              <Form.Item label={t("crate_stock.crate_barcode_label")}>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    ref={crateBarcodeRef}
                    size="large"
                    style={{ fontSize: 16 }}
                    value={barcodeInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBarcodeInput(val);
                      form.setFieldsValue({ crateId: val || undefined });
                      setCrateDropdownValue(null);
                    }}
                    placeholder={t("crate_stock.scan_or_enter_crate_barcode_placeholder")}
                    inputMode="numeric"
                    maxLength={CRATE_BARCODE_LENGTH}
                    allowClear
                  />
                  <Button size="large" onClick={handleCrateCheckClick} disabled={submitting}>
                    {t("crate_stock.check_crate")}
                  </Button>
                </Space.Compact>
              </Form.Item>

              <Form.Item label={t("crate_stock.available_crates_empty_label")}>
                <Select
                  ref={crateSelectRef}
                  size="large"
                  style={{ fontSize: 16 }}
                  value={crateDropdownValue}
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("crate_stock.select_empty_crate_placeholder")}
                  options={availableCrates.map((c) => ({
                    value: c.crateId,
                    label: c.crateId,
                  }))}
                  onChange={(value) => {
                    setCrateDropdownValue(value);
                    setBarcodeInput("");
                    form.setFieldsValue({ crateId: value });
                    setValidatedCrateId(String(value).trim());
                    setTimeout(() => crateBarcodeRef.current?.focus?.(), 0);
                  }}
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="crateId"
                hidden
                rules={[{ required: true, message: t("crate_stock.scan_or_select_crate") }]}
              >
                <Input />
              </Form.Item>

              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("crate_stock.crate_label")}
                    </Text>
                    <br />
                    <Text strong style={{ fontSize: 16 }}>
                      {String(watchedCrateId || "-")}
                    </Text>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("crate_stock.company_label")}
                    </Text>
                    <br />
                    <Text strong style={{ fontSize: 16 }}>
                      {detectedCompany || "-"}
                    </Text>
                  </div>
                </div>
              </Card>
            </>
          )
        ) : (
          <>
            <Form.Item
              name="crateId"
              hidden
              rules={[{ required: true, message: t("crate_stock.scan_or_select_crate") }]}
            >
              <Input />
            </Form.Item>

            <Form.Item label={t("crate_stock.crate_id_label")}>
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  ref={crateBarcodeRef}
                  value={barcodeInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBarcodeInput(val);
                    form.setFieldsValue({ crateId: val || undefined });
                    setCrateDropdownValue(null);
                  }}
                  placeholder={t("crate_stock.scan_crate_id_placeholder")}
                  inputMode="numeric"
                  maxLength={CRATE_BARCODE_LENGTH}
                  allowClear
                  disabled={isCrateLocked}
                />
                <Button size="large" onClick={handleCrateCheckClick} disabled={submitting || isCrateLocked}>
                  {t("crate_stock.check_crate")}
                </Button>
              </Space.Compact>
            </Form.Item>

            <Form.Item label={t("crate_stock.available_crates_empty_label")}>
              <Select
                value={crateDropdownValue}
                showSearch
                optionFilterProp="label"
                placeholder={t("crate_stock.select_empty_crate_placeholder")}
                disabled={isCrateLocked}
                options={availableCrates.map((c) => ({
                  value: c.crateId,
                  label: c.crateId,
                }))}
                onChange={(value) => {
                  setCrateDropdownValue(value);
                  setBarcodeInput("");
                  form.setFieldsValue({ crateId: value });
                  setValidatedCrateId(String(value).trim());
                  setTimeout(() => productSelectRef.current?.focus?.(), 0);
                }}
              />
            </Form.Item>
          </>
        )}

        {isProductStep && (
          <>
            {isMobileMode && !isProductLocked && (
              <div style={{ marginBottom: 12 }}>
                <Input
                  ref={productBarcodeRef}
                  size="large"
                  style={{ fontSize: 16 }}
                  placeholder={t("crate_stock.scan_product_barcode_placeholder")}
                  inputMode="numeric"
                  disabled={!isCrateValidated}
                  maxLength={MAX_PRODUCT_BARCODE_LENGTH}
                  onPressEnter={(e) => {
                    handleProductBarcodeScanned(e.target.value);
                    e.target.value = "";
                  }}
                />
              </div>
            )}

            {isMobileMode && isProductLocked ? (
              <>
                <Form.Item
                  name="productId"
                  hidden
                  rules={[{ required: true, message: t("crate_stock.select_product") }]}
                >
                  <Input />
                </Form.Item>

                <div
                  style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <div>
                    <Text strong>{t("crate_stock.product_label")}</Text>{" "}
                    <Text>
                      {selectedProduct?.productName ||
                        selectedProduct?.name?.en ||
                        selectedProduct?.name?.nl ||
                        selectedProductId}
                    </Text>
                  </div>
                  <Button
                    size="large"
                    onClick={() => {
                      form.setFieldsValue({ productId: undefined, quantity: 1 });
                      setTimeout(() => productBarcodeRef.current?.focus?.(), 0);
                    }}
                    disabled={!isCrateValidated}
                  >
                    {t("crate_stock.change")}
                  </Button>
                </div>
              </>
            ) : (
              <Form.Item
                name="productId"
                label={t("crate_stock.product_type_label")}
                rules={[{ required: true, message: t("crate_stock.select_product") }]}
              >
                <Select
                  ref={productSelectRef}
                  size="large"
                  style={{ fontSize: 16 }}
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("crate_stock.select_product_placeholder")}
                  disabled={!isCrateValidated}
                  options={filteredProducts.map((p) => ({
                    value: p.id,
                    label: `${p.productName || p.name?.en || p.name?.nl || p.id} (${
                      p.barcode || t("crate_stock.no_barcode")
                    })`,
                  }))}
                  onChange={(value) => {
                    const prod = products.find((p) => p.id === value);
                    if (prod) {
                      form.setFieldsValue({ quantity: 1 });
                      setTimeout(() => qtyRef.current?.focus?.(), 0);
                    }
                  }}
                />
              </Form.Item>
            )}

            <Form.Item
              name="quantity"
              label={isMobileMode ? null : t("crate_stock.quantity_label")}
              rules={[{ required: true, message: t("crate_stock.enter_quantity") }]}
            >
              {isMobileMode ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <MobileSectionLabel>{t("crate_stock.quantity_label")}</MobileSectionLabel>
                  <QuantityControl
                    productId={selectedProductId || "quantity"}
                    currentQuantity={Number(watchedQuantity || 0)}
                    isMobile
                    disabled={!isCrateValidated || !selectedProductId}
                    onIncrease={() => {
                      const next = Number(watchedQuantity || 0) + 1;
                      form.setFieldsValue({ quantity: next });
                    }}
                    onDecrease={() => {
                      const next = Math.max(1, Number(watchedQuantity || 1) - 1);
                      form.setFieldsValue({ quantity: next });
                    }}
                    onManualInput={(_, value) => {
                      const next = Math.max(1, Number(value || 1));
                      form.setFieldsValue({ quantity: next });
                    }}
                    inputRef={qtyRef}
                  />
                </div>
              ) : (
                <InputNumber
                  ref={qtyRef}
                  min={1}
                  style={{ width: "100%" }}
                  placeholder={t("crate_stock.quantity_placeholder")}
                  disabled={!isCrateValidated}
                  onPressEnter={() => {
                    // After qty, go to submit (footer) by natural tab order
                  }}
                />
              )}
            </Form.Item>

            <Form.Item label={isMobileMode ? null : t("crate_stock.package_date")}>
              {editingPackageDay ? (
                <DatePicker
                  autoFocus
                  open
                  allowClear={false}
                  format="DD-MM-YYYY"
                  value={packageDay}
                  onChange={(d) => {
                    if (d) setPackageDay(d);
                    setEditingPackageDay(false);
                  }}
                  onOpenChange={(isOpen) => !isOpen && setEditingPackageDay(false)}
                  style={{ width: "100%" }}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobileMode ? "space-between" : "flex-start",
                    gap: 8,
                  }}
                >
                  {isMobileMode && <Text>{t("crate_stock.package_date")}</Text>}
                  <Space size={4}>
                    <Text strong={isMobileMode}>{packageDay.format("DD-MM-YYYY")}</Text>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => setEditingPackageDay(true)}
                      disabled={submitting}
                    />
                  </Space>
                </div>
              )}
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddCrateStockModal;
